import { Component, HostListener, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { NzI18nService, en_US } from 'ng-zorro-antd/i18n';
import { NzModalService } from 'ng-zorro-antd/modal';
import { post1, registerSessionEndedNotifier, registerInfraErrorNotifier, attemptRefresh } from 'src/utils/api';
import { APP_HD_URL1 } from 'src/utils/urls';
import { NzMessageService } from 'ng-zorro-antd/message';
import { jwtDecode } from 'jwt-decode';
import { DataSharingService } from 'src/utils/broadcast-service';
import { Subscription } from 'rxjs';
import { SharedService } from './activity.service';
import { SwUpdate } from '@angular/service-worker';
import { LookupCacheService } from 'src/utils/lookup-cache.service';
import { ThemeService } from 'src/utils/theme.service';
import { recoverFromStaleChunk } from 'src/utils/stale-chunk-recovery';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnDestroy {
  title = 'Exception Portal';
  timeout = 300;
  idleTimeout: any;
  idleState = 'Active';
  idleTimeStart: any = '';
  timeoutPeriod = this.timeout * 1000; // 5 minutes in milliseconds

  subscription: Subscription;
  private versionUpdatesSub: Subscription;
  private unrecoverableSub: Subscription;
  private swUpdateInterval: any;
  private resetIdleDebounce: any;
  // Guards against stacking duplicate modals: checkSessionExp() polls every 30s and
  // multiple independent triggers (idle timeout, visibilitychange, expiry poll) can
  // all fire while one of these dialogs is already up.
  private idlePromptOpen = false;
  private sessionEndedShown = false;
  constructor(
    private router: Router,
    private i18n: NzI18nService,
    private modal: NzModalService,
    private message: NzMessageService,
    private dataSharingService: DataSharingService,
    private activityService: SharedService,
    private updates: SwUpdate,
    private lookupCache: LookupCacheService,
    private themeService: ThemeService
  ) {
    // api.ts's axios interceptor lives outside Angular's DI tree — this lets it show
    // a real in-app modal instead of a native window.alert() when a session truly ends
    // (e.g. the refresh token itself was rejected). Routed through endSession() so it
    // shares the same one-dialog-at-a-time guard as this component's own idle/expiry
    // checks instead of opening a second, independent modal for the same event.
    registerSessionEndedNotifier((message, onDismiss) => {
      this.endSession(message, onDismiss);
    });

    // Same wiring for infra failures (network down / DB unreachable) — one registration
    // here covers every screen's API calls, since they all go through api.ts's shared
    // axios instance.
    registerInfraErrorNotifier((message) => {
      this.message.create('error', message, { nzDuration: 6000 });
    });

    if (this.updates.isEnabled) {
      console.log('SW ENABLED');

      // immediate check
      this.updates.checkForUpdate().then((res) => {
        console.log('Initial check:', res);
      });

      // periodic check every 60 seconds so users get updates quickly after deployment
      this.swUpdateInterval = setInterval(() => {
        this.updates.checkForUpdate().then((res) => {
          console.log('Periodic check:', res);
        });
      }, 60000);

      // listen updates
      this.versionUpdatesSub = this.updates.versionUpdates.subscribe((event) => {
        console.log('SW EVENT:', event);

        if (event.type === 'VERSION_READY') {
          console.log('NEW VERSION AVAILABLE — activating update');
          this.updates.activateUpdate().then(() => {
            document.location.reload();
          });
        }

        if (event.type === 'VERSION_INSTALLATION_FAILED') {
          console.error('SW version installation failed — recovering by unregistering and reloading:', event.error);
          recoverFromStaleChunk();
        }
      });

      // Fires when the SW detects it can no longer serve this client reliably —
      // e.g. a lazy chunk referenced by an already-open tab 404s because a newer
      // deploy removed it. Without this, the tab is silently stuck on the old,
      // now-broken build until the user manually clears storage.
      this.unrecoverableSub = this.updates.unrecoverable.subscribe((event) => {
        console.error('SW entered an unrecoverable state — recovering by unregistering and reloading:', event.reason);
        recoverFromStaleChunk();
      });
    }
  }
  interval = null;
  ngOnInit() {
    document.addEventListener('visibilitychange', (e) => {
      if (document.visibilityState === 'visible') {
        if (this.updates.isEnabled) {
          this.updates.checkForUpdate();
        }
        if (
          window.location.href.indexOf('/login') == -1 &&
          window.location.href.indexOf('/register') == -1 &&
          window.location.pathname !== '/maintenance' &&
          !localStorage.getItem('clientId') &&
          !localStorage.getItem('token')
        ) {
          this.router.navigate(['/guest/login']);
        }
        if (this.idleTimeStart) {
          let currTime: any = new Date();
          let diff = currTime - this.idleTimeStart;
          let time = diff / 60000;
          let idle = localStorage.getItem('idle') ? parseInt(localStorage.getItem('idle')) : 0;
          if (time > idle && idle !== 0) {
            // Mirror the visible-tab idle path (runIdleLogic): give the user the same
            // Continue Session / Relogin choice instead of logging out outright. With a
            // short idle window, switching tabs for a few minutes is common and
            // shouldn't feel like an abrupt kick-out when the session is still
            // refreshable.
            this.promptIdleSession();
          }
        }
        this.idleTimeStart = '';
        // Re-check token expiry immediately on refocus rather than waiting for the
        // next 30s poll — background tabs get their timers throttled by the browser,
        // so a tab left hidden for hours could otherwise take a while to notice.
        this.checkSessionExp();
      } else if (document.visibilityState === 'hidden') {
        this.idleTimeStart = new Date();
      }
    });

    if (
      window.location.href.indexOf('/login') == -1 &&
      window.location.pathname !== '/maintenance' &&
      !localStorage.getItem('clientId') &&
      !localStorage.getItem('token')
    ) {
      this.router.navigate(['/guest/login']);
    }

    // 'exp' is stored as epoch milliseconds (Date.getTime()) at login/refresh — do not
    // multiply by 1000 again here, or expTime lands thousands of years in the future
    // and this check silently never fires (the bug behind "session never expires").
    let expIn: any = localStorage.getItem('exp');
    if (expIn !== null) {
      const expTime = new Date(Number(expIn));
      const currentTime = new Date();

      if (expTime < currentTime) {
        this.endSession('Your session has expired due to inactivity. Please sign in again to continue.');
      }
    }
    this.i18n.setLocale(en_US);
    this.checkSessionExp();
    this.interval = setInterval(() => {
      this.checkSessionExp();
    }, 30000);
    this.resetIdleTimer();
    this.subscription = this.dataSharingService.idleService$.subscribe((data) => {
      if (data) {
        this.resetIdleTimer();
      }
    });
  }
  @HostListener('document:mousemove')
  @HostListener('document:keydown')
  @HostListener('document:mousedown')
  @HostListener('document:touchstart')
  resetIdleTimer() {
    clearTimeout(this.idleTimeout);
    this.idleState = 'Active';
    clearTimeout(this.resetIdleDebounce);
    this.resetIdleDebounce = setTimeout(() => this.runIdleLogic(), 200);
  }

  private runIdleLogic() {
    let expIn: any = localStorage.getItem('exp');
    if (expIn !== null) {
      const expTime: any = new Date(Number(expIn));
      const currentTime: any = new Date();
      let diff = expTime - currentTime;
      let timer = diff / 60000;
      let idle: number = parseInt(localStorage.getItem('idle'));
      // The idle-prompt countdown length is the configured window (localStorage 'idle',
      // 30 min) full stop — it must not depend on how much access-token life is left.
      // Access tokens are short-lived (15 min, see api.ts), so "timer < idle" used to be
      // true on almost every call, and this.timeoutPeriod never got past its unused
      // 5-minute class default — re-arming a 5-minute countdown on every activity reset
      // instead of the intended 30, so the prompt kept firing during active use.
      this.timeoutPeriod = idle * 60 * 1000;
      // Token is about to expire (within 2 min) — refresh it silently now, independent of
      // the idle countdown above, so a real auth expiry doesn't cut the session short.
      if (timer < 2) {
        this.refreshToken(true);
      }
      if (currentTime - expTime >= 2 * 60 * 1000 && window.location.href.indexOf('/login') == -1) {
        this.endSession('Your session has expired due to inactivity. Please sign in again to continue.');
      } else {
        this.idleTimeout = setTimeout(() => {
          this.idleState = 'Idle';
          this.promptIdleSession();
        }, this.timeoutPeriod);
      }
    }
  }

  private logout = async () => {
    this.modal.closeAll();
    this.lookupCache.clear();
    // Best-effort server-side revoke — same call the explicit Sign Out button uses
    // (nav-right.component.ts's onLogoutClick). Without this, ending the session here
    // only cleared client-side storage: the refresh-token cookie/DB row stayed valid
    // server-side for its full lifetime, so it wasn't actually a "logout".
    await post1(APP_HD_URL1 + 'api/Authentication/logout', {});
    localStorage.clear();
    sessionStorage.clear();
    this.themeService.reset();
    // Full reload (not router.navigate) to tear down in-memory JS state — same
    // reasoning as api.ts's redirect() and nav-right's onLogoutClick.
    window.location.href = '/guest/login';
  };

  // Ends the session behind an explicit acknowledgement instead of a toast that
  // auto-dismisses while the route has already changed underneath it — mirrors the
  // modal api.ts's axios interceptor shows via registerSessionEndedNotifier, so every
  // "your session is over" path in the app (idle timeout, expired token, failed
  // refresh) gives the same deliberate "OK, take me to login" moment rather than
  // feeling like an abrupt kick-out.
  private endSession(message: string, onDismiss: () => void = this.logout) {
    if (this.sessionEndedShown) return;
    this.sessionEndedShown = true;
    this.modal.warning({
      nzTitle: 'Session Ended',
      nzContent: message,
      nzOnOk: () => onDismiss(),
      nzClosable: false
    });
  }

  // Fires once the idle window (localStorage 'idle', set at login) has elapsed with no
  // mouse/keyboard/touch activity. Instead of silently refreshing behind a toast, this
  // hands the user an explicit choice: Continue Session (refresh the token and keep
  // working) or Relogin (end the session now and go back to the login page). If
  // "Continue Session" itself fails (e.g. the refresh cookie is dead), refreshToken()
  // falls through to endSession() on its own.
  private promptIdleSession() {
    if (this.idlePromptOpen || !localStorage.getItem('token') || window.location.href.indexOf('/login') !== -1) {
      return;
    }
    this.idlePromptOpen = true;

    const AUTO_LOGOUT_SECONDS = 30;
    let secondsLeft = AUTO_LOGOUT_SECONDS;
    const contentFor = (seconds: number) =>
      `You've been idle for a while. Continue your session, or cancel to sign out. Signing you out automatically in ${seconds}s.`;

    const modalRef = this.modal.confirm({
      nzTitle: 'Still there?',
      nzContent: contentFor(secondsLeft),
      nzOkText: 'Continue Session',
      nzOnOk: () => {
        clearInterval(countdown);
        return this.refreshToken(false);
      },
      nzCancelText: 'Cancel',
      nzOnCancel: () => {
        clearInterval(countdown);
        return this.logout();
      },
      nzClosable: false
    });
    // Reset regardless of how it closed (Continue, Cancel, or the auto-logout timeout
    // below) so a later idle period can prompt again.
    modalRef.afterClose.subscribe(() => {
      this.idlePromptOpen = false;
    });

    // No response within the window this modal itself grants — auto-logout rather
    // than leaving the prompt open indefinitely (e.g. the tab was left open on this
    // dialog with nobody there to answer it).
    const countdown = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) {
        clearInterval(countdown);
        modalRef.destroy();
        this.logout();
        return;
      }
      modalRef.updateConfig({ nzContent: contentFor(secondsLeft) });
    }, 1000);
  }

  checkSessionExp = () => {
    let expIn: any = localStorage.getItem('exp');
    if (expIn !== null) {
      const expTime: any = new Date(Number(expIn));
      const currentTime: any = new Date();

      if (expTime < currentTime) {
        if (this.idleState === 'Active') {
          this.refreshToken(true);
        } else {
          this.promptIdleSession();
        }
      }
    }
  };
  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.swUpdateInterval) {
      clearInterval(this.swUpdateInterval);
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.dataSharingService.clearIdleService();
    }
    if (this.versionUpdatesSub) {
      this.versionUpdatesSub.unsubscribe();
    }
    if (this.unrecoverableSub) {
      this.unrecoverableSub.unsubscribe();
    }
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    if (this.resetIdleDebounce) {
      clearTimeout(this.resetIdleDebounce);
    }
    document.removeEventListener('visibilitychange', () => {});
  }

  refreshToken = async (silent: boolean) => {
    // Routed through api.ts's attemptRefresh so this proactive refresh shares the same
    // in-flight guard as the axios interceptor's reactive on-401 refresh — otherwise the
    // two can independently POST to /refresh within moments of each other (e.g. this
    // component's 30s expiry poll firing right as some unrelated call gets a 401), and
    // the backend's refresh-token reuse detection rejects the second one as stolen,
    // forcing a spurious logout. attemptRefresh already persists token/exp on success.
    const refreshed = await attemptRefresh();

    if (refreshed) {
      // Silent/background refreshes shouldn't interrupt the user with a success toast —
      // only the idle heads-up above is shown, refreshing itself is fully automatic.
      if (!silent) {
        this.message.create('success', 'You are still signed in.');
      }
    } else {
      // A stale/reused refresh cookie is rejected on purpose (reuse/theft
      // detection) — the session is over, so send the user back to login
      // instead of leaving them stuck behind an "Error updating session" toast.
      this.endSession('Your session has expired. Please sign in again to continue.');
    }
  };

  getDecodedAccessToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      return null;
    }
  }
}
